import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="splash--text-reveal"
// Splits heading text into words and reveals them with a staggered fade-up
// animation as they scroll into view.
//
// Usage:
//   <h2 data-controller="splash--text-reveal">Some heading text</h2>
export default class extends Controller {
  static values = {
    delay: { type: Number, default: 70 },
    threshold: { type: Number, default: 0.15 },
  };

  connect() {
    this.wrapWords();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.reveal();
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: this.thresholdValue },
    );
    this.observer.observe(this.element);
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
  }

  wrapWords() {
    const text = this.element.textContent.trim();
    this.element.textContent = "";
    this.element.classList.add("text-reveal");

    const words = text.split(/\s+/);
    words.forEach((word, i) => {
      const wrapper = document.createElement("span");
      wrapper.classList.add("text-reveal-word-wrap");

      const inner = document.createElement("span");
      inner.classList.add("text-reveal-word");
      inner.textContent = word;

      wrapper.appendChild(inner);
      this.element.appendChild(wrapper);

      // Add a space text node between words for natural inline spacing
      if (i < words.length - 1) {
        this.element.appendChild(document.createTextNode(" "));
      }
    });
  }

  reveal() {
    const words = this.element.querySelectorAll(".text-reveal-word");
    const count = words.length;
    const maxTotalStagger = 600;
    const perWord = Math.min(this.delayValue, count > 1 ? maxTotalStagger / (count - 1) : 0);

    words.forEach((word, i) => {
      // Ease-out curve: later words compress closer together
      const progress = count > 1 ? i / (count - 1) : 0;
      const eased = 1 - Math.pow(1 - progress, 1.8);
      word.style.transitionDelay = `${Math.round(eased * (count - 1) * perWord)}ms`;
      word.classList.add("revealed");
    });
  }
}
